import React from 'react'
import { Badge } from '@/components/common/Badge'
import { Button } from '@/components/common/Button'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionTitle } from '@/components/common/SectionTitle'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useUsuarios, type UsuarioListItem, type UsuarioRol } from '@/hooks/useUsuarios'
import './prototype.css'

const NewUserForm: React.FC<{
  onCreate: (input: { email: string; rol: UsuarioRol; sharePointUserId?: string | null }) => Promise<unknown>
}> = ({ onCreate }) => {
  const [email, setEmail] = React.useState('')
  const [rol, setRol] = React.useState<UsuarioRol>('comercial')
  const [sharePointUserId, setSharePointUserId] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setFormError(null)
    try {
      await onCreate({ email: email.trim().toLowerCase(), rol, sharePointUserId: sharePointUserId.trim() || null })
      setEmail('')
      setRol('comercial')
      setSharePointUserId('')
    } catch (err) {
      setFormError(
        err instanceof Error && err.message.includes('USER_ALREADY_EXISTS')
          ? 'Ya existe un usuario habilitado con ese correo.'
          : 'No se pudo agregar. Verifica el correo e intenta de nuevo.'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="prototypeContextCard" style={{ marginBottom: 16 }}>
      <SectionTitle
        title="Agregar usuario"
        subtitle="El correo debe corresponder a una cuenta Microsoft 365 ya creada en el tenant de Wacner."
      />
      <div className="prototypeRelationshipRow__grid">
        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">Correo</span>
          <input
            type="email"
            required
            placeholder="nombre.apellido@wacner.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">ID de usuario SharePoint</span>
          <input inputMode="numeric" placeholder="Se usa para ownership" value={sharePointUserId} onChange={(e) => setSharePointUserId(e.target.value)} />
        </label>
        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">Rol</span>
          <select value={rol} onChange={(e) => setRol(e.target.value as UsuarioRol)}>
            <option value="comercial">Comercial</option>
            <option value="administrador">Administrador</option>
          </select>
        </label>
      </div>
      {formError && <p className="prototypeRelationshipRow__summary" role="alert">{formError}</p>}
      <div className="prototypeContextCard__actions">
        <Button size="sm" type="submit" disabled={saving || !email.trim()}>
          {saving ? 'Agregando…' : 'Agregar usuario'}
        </Button>
      </div>
    </form>
  )
}

const UsuarioRow: React.FC<{
  usuario: UsuarioListItem
  isSelf: boolean
  onUpdate: (id: string, patch: { rol?: UsuarioRol; habilitado?: boolean }) => Promise<unknown>
}> = ({ usuario, isSelf, onUpdate }) => {
  const [rowError, setRowError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  const applyPatch = async (patch: { rol?: UsuarioRol; habilitado?: boolean }) => {
    setBusy(true)
    setRowError(null)
    try {
      await onUpdate(usuario.id, patch)
    } catch (err) {
      setRowError(
        err instanceof Error && err.message.includes('LAST_ADMIN_LOCKOUT')
          ? 'No puedes quitarte el acceso de administrador: quedarías sin forma de administrar Sales.'
          : 'No se pudo actualizar. Intenta de nuevo.'
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <article className="prototypeRelationshipRow">
      <div className="prototypeRelationshipRow__titleBlock">
        <span className="prototypeRelationshipRow__title">
          {usuario.email}
          {isSelf && <span className="prototypeMiniLabel"> · tú</span>}
        </span>
        <span className="prototypeRelationshipRow__responsible">
          <Badge label={usuario.habilitado ? 'Habilitado' : 'Deshabilitado'} variant={usuario.habilitado ? 'success' : 'info'} size="sm" />
        </span>
      </div>
      <div className="prototypeContextCard__actions">
        <select
          value={usuario.rol}
          disabled={busy}
          onChange={(e) => applyPatch({ rol: e.target.value as UsuarioRol })}
        >
          <option value="comercial">Comercial</option>
          <option value="administrador">Administrador</option>
        </select>
        <Button
          size="sm"
          variant={usuario.habilitado ? 'secondary' : 'primary'}
          disabled={busy}
          onClick={() => applyPatch({ habilitado: !usuario.habilitado })}
        >
          {usuario.habilitado ? 'Deshabilitar' : 'Habilitar'}
        </Button>
      </div>
      {rowError && <p className="prototypeRelationshipRow__summary" role="alert">{rowError}</p>}
    </article>
  )
}

export const Usuarios: React.FC = () => {
  const currentUser = useCurrentUser()
  const { status, usuarios, error, reload, create, update } = useUsuarios()
  const selfEmail = currentUser.data?.user.email?.toLowerCase()

  const renderBody = () => {
    if (status === 'loading') return <p className="prototypeRelationshipRow__summary">Cargando usuarios…</p>
    if (status === 'not_signed_in') return <p className="prototypeRelationshipRow__summary">Inicia sesión con tu cuenta Microsoft para administrar usuarios.</p>
    if (status === 'not_configured') return <p className="prototypeRelationshipRow__summary">La conexión con SharePoint aún no está habilitada en este entorno.</p>
    if (status === 'forbidden') {
      return (
        <p className="prototypeRelationshipRow__summary">
          Solo un administrador de Wacner Sales puede gestionar usuarios. Tu rol actual no tiene ese permiso.
        </p>
      )
    }
    if (status === 'unauthorized' || status === 'error') {
      return (
        <div>
          <p className="prototypeRelationshipRow__summary">
            No se pudieron cargar los usuarios{error instanceof Error ? `: ${error.message}` : '.'}
          </p>
          <Button size="sm" variant="secondary" onClick={reload}>Reintentar</Button>
        </div>
      )
    }

    return (
      <>
        <NewUserForm onCreate={create} />
        {usuarios.length === 0 ? (
          <p className="prototypeRelationshipRow__summary">Aún no hay usuarios habilitados.</p>
        ) : (
          <div className="prototypeRelationshipTray">
            {usuarios.map((u) => (
              <UsuarioRow key={u.id} usuario={u} isSelf={u.email.toLowerCase() === selfEmail} onUpdate={update} />
            ))}
          </div>
        )}
      </>
    )
  }

  return (
    <div className="prototypePage">
      <PageHeader
        title="Usuarios"
        subtitle="Quién puede entrar a Wacner Sales y con qué rol — autoadministrado, sin scripts."
      />
      <section className="prototypeSection">
        <SectionTitle
          title="Acceso a Wacner Sales"
          subtitle="La cuenta Microsoft 365 debe existir antes en el tenant; aquí solo se habilita dentro de Sales."
        />
        {renderBody()}
      </section>
    </div>
  )
}
